import { useState, useEffect, useRef, useCallback } from 'preact/hooks';

type FetchFunction<T> = (offset: number, limit: number) => Promise<{
  data: T[];
  total: number;
  count: number;
}>;

interface UseInfiniteScrollOptions {
  initialOffset?: number;
  limit?: number;
  threshold?: number;
}

export function useInfiniteScroll<T>(
  fetchFn: FetchFunction<T>,
  options: UseInfiniteScrollOptions = {}
) {
  const {
    initialOffset = 0,
    limit = 16,
    threshold = 0.5
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(initialOffset);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);

  const fetchData = useCallback(async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchFn(offset, limit);
      
      setData(prevData => [...prevData, ...response.data]);
      setOffset(prevOffset => prevOffset + response.count);
      setHasMore(response.count === limit && response.total > offset + response.count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, offset, limit, fetchFn]);

  const lastElementRef = useCallback((node: HTMLElement | null) => {
    if (loading) return;
    
    if (observer.current) {
      observer.current.disconnect();
    }
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchData();
      }
    }, { threshold });
    
    if (node) {
      observer.current.observe(node);
    }
  }, [loading, hasMore, fetchData, threshold]);

  useEffect(() => {
    fetchData();
    
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    hasMore,
    lastElementRef,
    refresh: () => {
      setData([]);
      setOffset(initialOffset);
      setHasMore(true);
      // fetchData will be called automatically by the effect
    }
  };
}
